import { useQuery } from '@tanstack/react-query'
import type { FormInputComponent } from '@viapeople/components'
import {
	ChoiceSelectField,
	cn,
	Forms9BoxRating,
	FormsCheckbox,
	FormsDivider,
	FormsHeader,
	FormsImage,
	FormsInstructions,
	FormsNumberField,
	FormsParagraph,
	FormsRating,
	FormsSelectField,
	FormsTextArea,
	FormsTextField,
	Modal,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import type { ReactNode } from 'react'
import { trpc } from '../../../utils/trpc'

// "View Full Evaluation" modal. Reuses the form-rendering primitives from
// `@viapeople/components` (the same ones the client app composes for
// cycle-execution) and renders them locked read-only. Falls back to the
// "Open in new tab ↗" link in the header when a component type isn't
// supported here yet — the deep-link goes to the canonical client view.

interface FormComponent {
	id: string
	name: string
	value?: string | number | boolean | null
	custom?: Record<string, unknown> | null
	children?: FormComponent[] | null
	write?: boolean
	optional?: boolean
	reportOnly?: boolean
	action?: string
	evaluationId?: string | null
}

interface TaskPayload {
	id: string
	formComponents?: FormComponent[]
	evaluation?: { cycleName?: string | null } | null
}

interface EvaluationViewResponse {
	taskId: string
	cycleId: string
	task: TaskPayload
}

interface HrReviewFlagsEvaluationModalProps {
	flagId: string
	employeeName: string
	deepLinkUrl: string
	onClose: () => void
}

export function HrReviewFlagsEvaluationModal({
	flagId,
	employeeName,
	deepLinkUrl,
	onClose,
}: HrReviewFlagsEvaluationModalProps) {
	const { data, isLoading, error } = useQuery({
		...trpc.admin.hrReviewFlags.getEvaluationView.queryOptions({ flagId }),
	})

	const view = data as EvaluationViewResponse | undefined
	const cycleName = view?.task?.evaluation?.cycleName ?? null

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`Evaluation — ${employeeName}`}
			fullContent
			// The Modal primitive already caps height at 90vh and adds its own
			// scroll, so we let the body grow naturally to fit the rendered form
			// without a redundant max-h here.
			bodyClassName='overflow-y-auto'
			className='w-[1180px] max-w-[95vw]'>
			<div className='flex flex-col gap-3 p-6'>
				<header className='flex items-start justify-between gap-3 border-b border-neutral-200 pb-3'>
					<div className='flex flex-col'>
						<Text variant='h6' weight='semibold' className='text-neutral-900'>
							{employeeName}
						</Text>
						{cycleName && (
							<Text variant='xsmall' className='text-neutral-500'>
								{cycleName}
							</Text>
						)}
					</div>
					<a
						href={deepLinkUrl}
						target='_blank'
						rel='noopener noreferrer'
						className='text-xs font-semibold text-primary-600 hover:underline'>
						Open in new tab ↗
					</a>
				</header>

				{isLoading && (
					<div className='py-12'>
						<SpinnerWithBackground />
					</div>
				)}

				{!isLoading && error && (
					<div className='rounded-md border border-error-200 bg-error-50 p-4'>
						<Text variant='small' className='text-error-700'>
							Could not load evaluation: {error.message}
						</Text>
					</div>
				)}

				{!isLoading && !error && view && (
					<div className='flex flex-col gap-4'>
						{(view.task.formComponents ?? [])
							.filter((entry) => entry.action !== 'delete')
							.map((entry) => (
								<ReadOnlyComponent key={entry.id} component={entry} />
							))}
					</div>
				)}
			</div>
		</Modal>
	)
}

/**
 * Recursive read-only renderer. Mirrors the client app's ComponentRenderer
 * but stripped to the read-only path: every form field is rendered with
 * `readOnly={true}` and `value` taken straight from the hydrated payload.
 * Unsupported component types render a small placeholder with their name
 * so HR can still see something useful (and the "Open in new tab" link
 * remains the canonical full view).
 */
// The form components from `@viapeople/components` are typed against the
// strict `FormInputComponent` (NestedChildren) shape, but the payload we
// get from `admin.hrReviewFlags.getEvaluationView` carries the same fields
// plus a hydrated `value`. Cast at the boundary so the strict prop type is
// satisfied without making us re-type every Drizzle column.
const asFormInput = (c: FormComponent): FormInputComponent => c as unknown as FormInputComponent

function ReadOnlyComponent({ component }: { component: FormComponent }): ReactNode {
	const value = component.value ?? ''
	const readOnly = true
	const c = asFormInput(component)

	const wrap = (node: ReactNode) => <div className={cn(!component.write && 'opacity-90')}>{node}</div>

	switch (component.name) {
		case 'container':
			return <ReadOnlyContainer component={component} />
		case 'repeatable':
			return <ReadOnlyContainer component={component} />
		case 'heading':
			return <FormsHeader component={c} />
		case 'instructions':
			return <FormsInstructions component={c} />
		case 'paragraph':
			return <FormsParagraph component={c} />
		case 'divider':
			return <FormsDivider component={c} />
		case 'image':
			return <FormsImage component={c} />
		case 'text':
			return wrap(
				<FormsTextField
					component={c}
					value={String(value)}
					onChange={() => {}}
					readOnly={readOnly}
					required={false}
					hasError={false}
				/>,
			)
		case 'textarea':
			return wrap(
				<FormsTextArea
					component={c}
					value={String(value)}
					onChange={() => {}}
					readOnly={readOnly}
					required={false}
					hasError={false}
				/>,
			)
		case 'number':
			return wrap(
				<FormsNumberField
					component={c}
					value={String(value)}
					onChange={() => {}}
					readOnly={readOnly}
					required={false}
					hasError={false}
				/>,
			)
		case 'rating':
			return wrap(
				<FormsRating
					component={c}
					value={String(value)}
					onChange={() => {}}
					readOnly={readOnly}
					required={false}
					hasError={false}
				/>,
			)
		case 'select':
			return wrap(
				<FormsSelectField
					component={c}
					value={String(value)}
					onChange={() => {}}
					readOnly={readOnly}
					required={false}
					hasError={false}
				/>,
			)
		case 'radio-group':
			return wrap(
				<ChoiceSelectField
					component={c}
					value={String(value)}
					onChange={() => {}}
					readOnly={readOnly}
					required={false}
					hasError={false}
				/>,
			)
		case 'checkbox':
			return wrap(
				<FormsCheckbox
					component={c}
					checked={Boolean(value)}
					onChange={() => {}}
					readOnly={readOnly}
					required={false}
					hasError={false}
				/>,
			)
		case '9boxrating':
			return wrap(
				<Forms9BoxRating
					component={c}
					value={typeof value === 'number' ? value : Number.parseInt(String(value || 0), 10) || undefined}
					onChange={() => {}}
					required={false}
					hasError={false}
				/>,
			)
		case 'hrreviewflag':
			// The HR Review Flag widget is the entry point for this whole queue —
			// rendering it inline would be visually noisy and self-referential, so
			// just show a small breadcrumb instead.
			return (
				<div className='rounded-md border border-neutral-200 bg-neutral-50 p-3'>
					<Text variant='xsmall' className='text-neutral-500'>
						HR Review Flag widget
					</Text>
				</div>
			)
		default:
			return (
				<div className='rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-3'>
					<Text variant='xsmall' className='text-neutral-500'>
						{component.name} (preview not available — open in new tab to view)
					</Text>
				</div>
			)
	}
}

function ReadOnlyContainer({ component }: { component: FormComponent }) {
	const props = (component.custom ?? {}) as { label?: string; isSection?: boolean }
	const children = (component.children ?? []).filter((c) => c.action !== 'delete')

	return (
		<section
			className={cn('flex flex-col gap-3', props.isSection && 'rounded-lg border border-neutral-200 bg-white p-4')}>
			{props.label && (
				<Text variant='small' weight='semibold' className='text-neutral-800'>
					{props.label}
				</Text>
			)}
			{children.map((child) => (
				<ReadOnlyComponent key={child.id} component={child} />
			))}
		</section>
	)
}
